/**
 * KPIAssignmentCard — Card displaying a single KPI assignment.
 *
 * Designed for a 2-column grid layout. Shows at a glance:
 *  - Status, category, cadence, weight
 *  - Progress percentage (hero metric) with trend indicator
 *  - Check-in timeline bar and sparkline
 *  - Last check-in time and next due date
 *  - Quick check-in action button
 */
import React, { useMemo } from 'react';
import CalendarDays from 'lucide-react/dist/esm/icons/calendar-days';
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up';
import TrendingDown from 'lucide-react/dist/esm/icons/trending-down';
import Clock from 'lucide-react/dist/esm/icons/clock';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign';
import Send from 'lucide-react/dist/esm/icons/send';
import type { KPIAssignment } from '../../../services/api/kpi';
import KPIStatusBadge from './KPIStatusBadge';
import KPIProgressChart from './KPIProgressChart';

const CATEGORY_COLORS: Record<string, string> = {
  financial: 'text-emerald-400 bg-emerald-400/10',
  sales: 'text-blue-400 bg-blue-400/10',
  marketing: 'text-purple-400 bg-purple-400/10',
  operations: 'text-amber-400 bg-amber-400/10',
  product: 'text-cyan-400 bg-cyan-400/10',
  hr: 'text-pink-400 bg-pink-400/10',
  customer_success: 'text-orange-400 bg-orange-400/10',
  custom: 'text-content-tertiary bg-surface-primary',
};

const CADENCE_DAYS: Record<string, number> = {
  weekly: 7,
  biweekly: 14,
  monthly: 30,
  quarterly: 91,
};

const LATE_THRESHOLD_DAYS = 3;

interface CheckInSlot {
  dueDate: Date;
  status: 'completed' | 'late' | 'upcoming' | 'current';
}

function computeCheckInSlots(
  cycleStart: string | undefined,
  cycleEnd: string | undefined,
  cadence: string | undefined,
  checkInCount: number,
  lastCheckIn: string | undefined,
): CheckInSlot[] {
  if (!cycleStart || !cycleEnd || !cadence) return [];
  const interval = CADENCE_DAYS[cadence];
  if (!interval) return [];

  const start = new Date(cycleStart + 'T00:00:00');
  const end = new Date(cycleEnd + 'T23:59:59');
  const now = new Date();

  const slots: CheckInSlot[] = [];
  const current = new Date(start);
  current.setDate(current.getDate() + interval);

  while (current <= end) {
    slots.push({ dueDate: new Date(current), status: 'upcoming' });
    current.setDate(current.getDate() + interval);
  }

  if (slots.length === 0) {
    slots.push({ dueDate: new Date(end), status: 'upcoming' });
  }

  const completed = Math.min(checkInCount, slots.length);
  for (let i = 0; i < completed; i++) {
    slots[i].status = 'completed';
  }

  for (let i = completed; i < slots.length; i++) {
    const due = slots[i].dueDate;
    if (now > due) {
      const daysLate = Math.floor((now.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
      slots[i].status = daysLate >= LATE_THRESHOLD_DAYS ? 'late' : 'current';
    } else {
      if (i === completed) {
        slots[i].status = 'current';
      }
      break;
    }
  }

  return slots;
}

function relativeTime(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'today';
  if (days === 1) return '1d ago';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

interface KPIAssignmentCardProps {
  assignment: KPIAssignment;
  onClick: (id: string) => void;
  onCheckIn?: (id: string) => void;
  showAssignee?: boolean;
}

const KPIAssignmentCard: React.FC<KPIAssignmentCardProps> = ({
  assignment,
  onClick,
  onCheckIn,
  showAssignee = false,
}) => {
  const a = assignment;

  const slots = useMemo(() => computeCheckInSlots(
    a.cycle_start_date,
    a.cycle_end_date,
    a.definition_cadence,
    a.check_in_count ?? 0,
    a.last_check_in,
  ), [a.cycle_start_date, a.cycle_end_date, a.definition_cadence, a.check_in_count, a.last_check_in]);

  const slotsCompleted = slots.filter(s => s.status === 'completed').length;
  const lateCount = slots.filter(s => s.status === 'late').length;
  const totalSlots = slots.length;
  const hasLate = lateCount > 0;
  const actualCheckIns = a.check_in_count ?? slotsCompleted;

  const trend = useMemo(() => {
    if (!a.check_in_history || a.check_in_history.length < 2) return null;
    const last = a.check_in_history[a.check_in_history.length - 1];
    const prev = a.check_in_history[a.check_in_history.length - 2];
    const lastVal = last.percentage ?? last.value;
    const prevVal = prev.percentage ?? prev.value;
    if (lastVal > prevVal) return 'up' as const;
    if (lastVal < prevVal) return 'down' as const;
    return 'flat' as const;
  }, [a.check_in_history]);

  const lastCheckInLabel = a.last_check_in ? relativeTime(a.last_check_in) : null;

  const nextDue = useMemo(() => {
    const next = slots.find(s => s.status === 'current' || s.status === 'upcoming');
    if (!next) return null;
    const diffMs = next.dueDate.getTime() - Date.now();
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (days < -1) return { label: `${Math.abs(days)}d overdue`, urgent: true };
    if (days < 0 || days === 0) return { label: 'due today', urgent: true };
    if (days === 1) return { label: 'tomorrow', urgent: false };
    return { label: `in ${days}d`, urgent: false };
  }, [slots]);

  const showWeight = a.weight > 0 && a.weight !== 100;

  const formatValue = (val: number | null) => {
    if (val === null || val === undefined) return '—';
    const label = a.definition_unit_label || '';
    if (a.definition_unit === 'percentage') return `${val}${label}`;
    if (a.definition_unit === 'currency') return `${label}${val.toLocaleString()}`;
    return `${val.toLocaleString()}${label ? ` ${label}` : ''}`;
  };

  const pctColor =
    (a.current_percentage ?? 0) >= 80 ? 'text-emerald-400' :
    (a.current_percentage ?? 0) >= 50 ? 'text-amber-400' : 'text-status-error';

  return (
    <button
      type="button"
      onClick={() => onClick(a.id)}
      className={`
        w-full text-left p-4 rounded-xl border transition-all cursor-pointer outline-none group flex flex-col
        ${hasLate
          ? 'bg-status-error/[0.04] border-status-error/15 hover:border-status-error/25'
          : 'bg-surface-primary border-subtle hover:border-default'
        }
        hover:bg-surface-secondary
      `}
    >
      {/* Header: category + cadence + weight + status */}
      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${CATEGORY_COLORS[a.definition_category || 'custom'] || CATEGORY_COLORS.custom}`}>
          {(a.definition_category || 'custom').replace('_', ' ')}
        </span>
        {a.definition_cadence && (
          <span className="flex items-center gap-0.5 text-[10px] text-content-muted">
            <CalendarDays size={10} />
            {a.definition_cadence}
          </span>
        )}
        {showWeight && (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium text-content-tertiary bg-surface-secondary">
            {a.weight}% wt
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          {hasLate && (
            <span className="flex items-center gap-0.5 text-[10px] text-status-error font-medium">
              <AlertCircle size={10} />
              {lateCount} late
            </span>
          )}
          <KPIStatusBadge
            percentage={a.current_percentage}
            status={a.status}
            thresholds={a.definition_thresholds}
          />
        </span>
      </div>

      {/* Name */}
      <h4 className="text-sm font-medium text-content line-clamp-2 mb-1">
        {a.definition_name || 'Unnamed KPI'}
      </h4>

      {/* Assignee (team view) */}
      {showAssignee && a.assignee_name && (
        <p className="text-[11px] text-content-tertiary mb-1">{a.assignee_name}</p>
      )}

      {/* Metrics: hero percentage + trend + current/target */}
      <div className="flex items-end justify-between mt-auto pt-2">
        <div className="flex items-baseline gap-1.5">
          {a.current_percentage !== null ? (
            <span className={`text-2xl font-bold tabular-nums ${pctColor}`}>
              {Math.round(a.current_percentage ?? 0)}%
            </span>
          ) : (
            <span className="text-2xl font-bold text-content-muted tabular-nums">—</span>
          )}
          {trend === 'up' && <TrendingUp size={14} className="text-emerald-400/60" />}
          {trend === 'down' && <TrendingDown size={14} className="text-status-error/60" />}
        </div>
        <span className="text-[11px] text-content-tertiary tabular-nums text-right">
          {formatValue(a.current_value)}
          <span className="text-content-muted"> / </span>
          {formatValue(a.target_value)}
        </span>
      </div>

      {/* Financial impact badge */}
      {(a.financial_impact_target || a.financial_impact_actual) && (
        <div className="flex items-center gap-1.5 mt-1.5 text-[11px]">
          <DollarSign size={10} className="text-emerald-400 shrink-0" />
          {a.financial_impact_actual != null && a.financial_impact_actual > 0 ? (
            <span className="text-emerald-400 font-medium tabular-nums">
              ${a.financial_impact_actual.toLocaleString()}
            </span>
          ) : (
            <span className="text-content-muted tabular-nums">$0</span>
          )}
          {a.financial_impact_target != null && (
            <span className="text-content-muted tabular-nums">
              / ${a.financial_impact_target.toLocaleString()} target
            </span>
          )}
        </div>
      )}

      {/* Timeline bar + sparkline */}
      {totalSlots > 0 && (
        <div className="mt-3">
          <div className="flex gap-[2px] h-1.5 rounded-full overflow-hidden">
            {slots.map((slot, i) => (
              <div
                key={i}
                className={`flex-1 rounded-sm transition-colors
                  ${slot.status === 'completed' ? 'bg-blue-400/40' : ''}
                  ${slot.status === 'late' ? 'bg-status-error/50' : ''}
                  ${slot.status === 'current' ? 'bg-blue-400/25' : ''}
                  ${slot.status === 'upcoming' ? 'bg-surface-secondary' : ''}
                `}
                title={`${slot.dueDate.toLocaleDateString()} — ${slot.status}`}
              />
            ))}
          </div>
          {a.check_in_history && a.check_in_history.length >= 2 && (
            <div className="mt-1.5 -mx-1">
              <KPIProgressChart
                checkIns={a.check_in_history}
                targetValue={a.target_value}
                baselineValue={a.baseline_value}
                currentPercentage={a.current_percentage}
                unit={a.definition_unit}
                unitLabel={a.definition_unit_label}
                height={44}
                variant="compact"
              />
            </div>
          )}
        </div>
      )}

      {/* Fallback if no slots */}
      {totalSlots === 0 && (
        <div className="flex items-center gap-4 mt-2 text-[11px] text-content-tertiary">
          <span>Current: <span className="text-content-secondary font-medium">{formatValue(a.current_value)}</span></span>
          <span>Target: <span className="text-content-secondary">{formatValue(a.target_value)}</span></span>
        </div>
      )}

      {/* Footer: timing info + check-in count + quick action */}
      <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-subtle">
        <div className="flex items-center gap-2.5 text-[11px] text-content-muted">
          {lastCheckInLabel && (
            <span className="flex items-center gap-1">
              <Clock size={10} className="text-content-muted" />
              {lastCheckInLabel}
            </span>
          )}
          {nextDue && (
            <span className={nextDue.urgent ? 'text-status-error/80 font-medium' : ''}>
              {nextDue.urgent ? '⏱ ' : '→ '}{nextDue.label}
            </span>
          )}
          {!lastCheckInLabel && !nextDue && (
            <span>No check-ins yet</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {totalSlots > 0 && (
            <span className="text-[11px] text-content-muted tabular-nums">
              {actualCheckIns}/{totalSlots}
            </span>
          )}
          {onCheckIn && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); onCheckIn(a.id); }}
              className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium text-status-success bg-status-success/10 hover:bg-status-success/20 transition-colors border border-status-success/15"
            >
              <Send size={10} />
              Check In
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

export default React.memo(KPIAssignmentCard);
